// src/utils/errorReporter.js
// Reporter de errores liviano. Si REACT_APP_SENTRY_DSN esta seteada manda eventos
// al endpoint envelope de Sentry via fetch (sin SDK, sin agregar deps). Si no esta
// seteada, los errores quedan en console.error y nunca rompen la UI.
//
// Formato del DSN: https://<key>@<host>/<projectId>
// Endpoint: https://<host>/api/<projectId>/envelope/?sentry_key=<key>&sentry_version=7

const DSN = process.env.REACT_APP_SENTRY_DSN || '';
const ENV = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'production';
const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || 'avlink-frontend';

let parsed = null;
try {
  if (DSN) {
    const u = new URL(DSN);
    parsed = {
      publicKey: u.username,
      host: u.host,
      projectId: u.pathname.replace(/^\//, ''),
      envelopeUrl: `${u.protocol}//${u.host}/api/${u.pathname.replace(/^\//, '')}/envelope/?sentry_key=${u.username}&sentry_version=7`
    };
  }
} catch (e) {
  // DSN invalido: lo ignoramos y trabajamos solo con console
  parsed = null;
}

const userContext = { id: null, email: null };

export function setUserContext({ id, email } = {}) {
  userContext.id = id || null;
  userContext.email = email || null;
}

export function clearUserContext() {
  userContext.id = null;
  userContext.email = null;
}

function buildEvent(error, context = {}) {
  const isError = error instanceof Error;
  const message = isError ? error.message : String(error);
  return {
    event_id: cryptoRandomId(),
    timestamp: Date.now() / 1000,
    platform: 'javascript',
    environment: ENV,
    release: RELEASE,
    level: context.level || 'error',
    logger: 'frontend',
    server_name: typeof window !== 'undefined' ? window.location?.host : 'unknown',
    request: typeof window !== 'undefined' ? {
      url: window.location?.href,
      headers: { 'User-Agent': window.navigator?.userAgent || '' }
    } : undefined,
    tags: context.tags || {},
    extra: context.extra || {},
    user: userContext.id ? { id: userContext.id, email: userContext.email } : undefined,
    exception: isError ? {
      values: [{
        type: error.name || 'Error',
        value: message,
        stacktrace: parseStack(error.stack)
      }]
    } : undefined,
    message: !isError ? { formatted: message } : undefined
  };
}

function parseStack(stack) {
  if (!stack) return undefined;
  const frames = stack.split('\n').slice(1).map((line) => {
    const m = /at\s+(?:(.+?)\s+\()?(.*?):(\d+):(\d+)\)?/.exec(line.trim());
    if (!m) return null;
    return {
      function: m[1] || '?',
      filename: m[2],
      lineno: Number(m[3]),
      colno: Number(m[4]),
      in_app: !/node_modules|webpack|\(internal/.test(m[2])
    };
  }).filter(Boolean).reverse();
  return frames.length ? { frames } : undefined;
}

function cryptoRandomId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '');
  } catch { /* fallback */ }
  let s = '';
  for (let i = 0; i < 32; i += 1) s += Math.floor(Math.random() * 16).toString(16);
  return s;
}

async function send(event) {
  if (!parsed) return;
  try {
    const envelopeHeader = JSON.stringify({
      event_id: event.event_id,
      sent_at: new Date().toISOString(),
      dsn: DSN
    });
    const itemHeader = JSON.stringify({ type: 'event' });
    const body = `${envelopeHeader}\n${itemHeader}\n${JSON.stringify(event)}`;
    // No bloqueamos la app si Sentry no responde.
    await fetch(parsed.envelopeUrl, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
      // keepalive permite que se envie aun durante navegacion.
      keepalive: true
    });
  } catch {
    // tragamos: el reporter NUNCA debe tirar.
  }
}

export function captureException(error, context = {}) {
  try {
    console.error('[errorReporter]', error, context.extra || '');
    if (parsed) send(buildEvent(error, context));
  } catch { /* swallow */ }
}

export function captureMessage(message, context = {}) {
  try {
    if (context.level !== 'info') console.warn('[errorReporter]', message, context.extra || '');
    if (parsed) send(buildEvent(message, { ...context, level: context.level || 'warning' }));
  } catch { /* swallow */ }
}

export function initErrorReporter() {
  if (typeof window === 'undefined') return;
  // Errors no manejados
  window.addEventListener('error', (ev) => {
    captureException(ev.error || ev.message || 'window.onerror', {
      tags: { source: 'window.onerror' },
      extra: { filename: ev.filename, line: ev.lineno, col: ev.colno }
    });
  });
  // Promise rejections sin catch
  window.addEventListener('unhandledrejection', (ev) => {
    captureException(ev.reason || 'unhandledrejection', {
      tags: { source: 'unhandledrejection' }
    });
  });
}

const errorReporter = { captureException, captureMessage, initErrorReporter, setUserContext, clearUserContext };
export default errorReporter;
